import { keys } from '../keys';
import {
  createBillingConfig,
  PriceInterval,
  PriceModel,
  PriceType
} from './schema';

enum Feature {
  // Core Features
  LiveTutoring = 'Live 1-on-1 Tutoring',
  GroupClasses = 'Group Classes',
  RecordedSessions = 'Recorded Sessions',
  HomeworkAssignments = 'Homework & Assignments',
  ProgressTracking = 'Progress Tracking',

  // Pro Features
  UnlimitedStudents = 'Unlimited Students',
  AdvancedAnalytics = 'Advanced Analytics',
  CustomBranding = 'Custom Branding',
  ParentPortal = 'Parent Portal Access',

  // Business Features
  MultipleOrganizations = 'Multiple Organizations',
  APIAccess = 'API Access',
  WhiteLabel = 'White Label Options',
  DedicatedSupport = 'Dedicated Support',
  BulkEnrollment = 'Bulk Enrollment',
  CustomIntegrations = 'Custom Integrations'
}

const currency = 'USD';

export const billingConfig = createBillingConfig({
  products: [
    {
      id: 'free',
      name: 'Free',
      description: 'Perfect for individual tutors getting started.',
      label: 'Get started free',
      isFree: true,
      features: [
        'Up to 5 students',
        Feature.LiveTutoring,
        Feature.HomeworkAssignments,
        Feature.ProgressTracking,
        'Basic scheduling',
        '1-hour session limit'
      ],
      plans: [
        {
          id: 'plan-free-month',
          displayIntervals: [PriceInterval.Month],
          prices: [
            {
              id: 'price-free-month-id',
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              interval: PriceInterval.Month,
              cost: 0,
              currency
            }
          ]
        },
        {
          id: 'plan-free-year',
          displayIntervals: [PriceInterval.Year],
          prices: [
            {
              id: 'price-free-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 0,
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'pro',
      name: 'Pro',
      description: 'Best for professional tutors and small education centers.',
      label: 'Start free trial',
      recommended: true,
      features: [
        'Up to 60 students',
        Feature.LiveTutoring,
        Feature.GroupClasses,
        Feature.HomeworkAssignments,
        Feature.ProgressTracking,
        Feature.AdvancedAnalytics,
        Feature.RecordedSessions,
        Feature.MultipleOrganizations,
        '3-hour session duration',
        '1 TB storage space',
        'Priority support'
      ],
      plans: [
        {
          id: 'plan-pro-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 14, // Free trial for Pro plan
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_MONTH_ID ||
                'price-pro-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 199,
              currency
            }
          ]
        },
        {
          id: 'plan-pro-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 14, // Free trial for Pro plan
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_PRO_YEAR_ID ||
                'price-pro-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 1990, // 2 months free with yearly billing (29 * 10 = 290)
              currency
            }
          ]
        }
      ]
    },
    {
      id: 'business',
      name: 'Business',
      description: 'For education organizations and tutoring centers.',
      label: 'Start free trial',
      features: [
        'Up to 100 students',
        Feature.LiveTutoring,
        Feature.GroupClasses,
        Feature.RecordedSessions,
        Feature.HomeworkAssignments,
        Feature.ProgressTracking,
        Feature.AdvancedAnalytics,
        Feature.MultipleOrganizations,
        'API Access (upcoming)',
        Feature.DedicatedSupport,
        Feature.BulkEnrollment,
        '3-hour session duration',
        '5 TB storage space',
        'Custom integrations'
      ],
      plans: [
        {
          id: 'plan-business-month',
          displayIntervals: [PriceInterval.Month],
          trialDays: 14, // Free trial for Business plan
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_BUSINESS_MONTH_ID ||
                'price-business-month-id',
              interval: PriceInterval.Month,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 299,
              currency
            }
          ]
        },
        {
          id: 'plan-business-year',
          displayIntervals: [PriceInterval.Year],
          trialDays: 14, // Free trial for Business plan
          prices: [
            {
              id:
                keys().NEXT_PUBLIC_BILLING_PRICE_BUSINESS_YEAR_ID ||
                'price-business-year-id',
              interval: PriceInterval.Year,
              type: PriceType.Recurring,
              model: PriceModel.Flat,
              cost: 2990, // 2 months free with yearly billing (99 * 10 = 990)
              currency
            }
          ]
        }
      ]
    }
  ]
});

export const billingConfigDisplayIntervals = Array.from(
  new Set(
    billingConfig.products
      .filter((product) => !product.hidden)
      .flatMap((product) =>
        product.plans.flatMap((plan) => plan.displayIntervals)
      )
      .filter(Boolean)
  )
) as PriceInterval[];
